import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useInventoryItem, useUpdateInventoryItem } from "../../api/inventory";
import { Alert } from "../../components/ui/Alert";
import BackButton from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Field } from "../../components/ui/Field";
import { FilterBar } from "../../components/ui/FilterBar";
import { Form } from "../../components/ui/Form";
import { Page } from "../../components/ui/Page";
import PageLoader from "../../components/ui/PageLoader";
import { Section } from "../../components/ui/Section";

import { UserSelect } from "../../components/ui/UserSelect";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { useTranslation } from "../../hooks/useTranslation";
import type { InventoryAudience, InventoryItemResponse, UpdateInventoryItemRequest } from "../../types/inventory";
import type { UserLightResponse } from "../../types/user";
import { hasTrimmedValue, isOptionalHttpUrl } from "../../utils/formValidation";
import styles from "./EditInventoryPage.module.css";

type RejectCause =
  | "not_interesting"
  | "duplicate"
  | "non_compliant"
  | "out_of_scope"
  | "insufficient_info"
  | "not_sustainable"
  | "other"
  | "";

interface EditFormValues {
  name: string;
  description: string;
  improvementNotes: string;
  githubUrl: string;
  deploymentLocation: string;
  audience: InventoryAudience;
  participants: UserLightResponse[];
  mgmtStatus: InventoryItemResponse["status"];
  rejectCause: RejectCause;
  rejectNote: string;
}

function toFormValues(item: InventoryItemResponse): EditFormValues {
  return {
    name: item.name,
    description: item.description,
    improvementNotes: item.improvementNotes ?? "",
    githubUrl: item.githubUrl ?? "",
    deploymentLocation: item.deploymentLocation ?? "",
    audience: item.audience,
    participants: item.participants,
    mgmtStatus: item.status,
    rejectCause: (item.rejectCause ?? "") as RejectCause,
    rejectNote: item.rejectNote ?? "",
  };
}

export default function EditInventoryPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: item, isLoading } = useInventoryItem(Number(id) ?? 0);
  useLayoutTitle(
    item ? t("inventory.editInventoryPageTitle", { id: item.id, title: item.name }) : t("inventory.allItems"),
  );
  const currentUser = useCurrentUser();
  const updateItem = useUpdateInventoryItem(Number(id) ?? 0);

  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<EditFormValues>({
    values: item ? toFormValues(item) : undefined,
  });

  const audience = watch("audience");
  const mgmtStatus = watch("mgmtStatus");
  const isRejected = mgmtStatus === "rejected";

  if (isLoading) return <PageLoader />;
  if (!item) return null;

  const isAdmin = currentUser?.isAdmin === true;
  const isAuthor = currentUser?.id === item.createdBy.id;

  if (!isAdmin && !isAuthor) {
    navigate(`/inventory/${id}`, { replace: true });
    return null;
  }

  const rejectCauseOptions: { value: RejectCause; label: string }[] = [
    { value: "not_interesting", label: t("inventory.rejectCauseNotInteresting") },
    { value: "duplicate", label: t("inventory.rejectCauseDuplicate") },
    { value: "non_compliant", label: t("inventory.rejectCauseNonCompliant") },
    { value: "out_of_scope", label: t("inventory.rejectCauseOutOfScope") },
    { value: "insufficient_info", label: t("inventory.rejectCauseInsufficientInfo") },
    { value: "not_sustainable", label: t("inventory.rejectCauseNotSustainable") },
    { value: "other", label: t("inventory.rejectCauseOther") },
  ];

  const statusOptions: { value: InventoryItemResponse["status"]; label: string }[] = [
    { value: "under_review", label: t("inventory.statusUnderReview") },
    { value: "taken_over", label: t("inventory.statusTakenOver") },
    { value: "completed", label: t("inventory.statusCompleted") },
    { value: "rejected", label: t("inventory.statusRejected") },
  ];

  const onSubmit = handleSubmit((values) => {
    const payload: UpdateInventoryItemRequest = {
      name: values.name.trim(),
      description: values.description.trim(),
      audience: values.audience,
      participantIds: values.participants.map((p) => p.id),
      ...(values.improvementNotes.trim() ? { improvementNotes: values.improvementNotes.trim() } : {}),
      ...(values.githubUrl.trim() ? { githubUrl: values.githubUrl.trim() } : {}),
      ...(values.deploymentLocation.trim() ? { deploymentLocation: values.deploymentLocation.trim() } : {}),
    };

    if (isAdmin) {
      payload.status = values.mgmtStatus;
      if (values.mgmtStatus === "rejected") {
        if (values.rejectCause) payload.rejectCause = values.rejectCause;
        if (values.rejectNote.trim()) payload.rejectNote = values.rejectNote.trim();
      }
    }

    updateItem.mutate(payload, { onSuccess: () => navigate(`/inventory/${id}`) });
  });

  return (
    <Page width="wide">
      <Card>
        <Form noValidate onSubmit={onSubmit}>
          <div className={styles.cardHeaderActions}>
            <div>
              <BackButton label={t("inventory.backToInventory")} onClick={() => navigate(`/inventory/${id}`)} />
            </div>
          </div>

          <Field.Input
            label={t("inventory.name")}
            error={errors.name?.message}
            {...register("name", {
              validate: (value) => hasTrimmedValue(value) || t("common.required"),
            })}
          />
          <Field.Textarea
            label={t("inventory.description")}
            info={t("common.descriptionHint")}
            infoTitle={t("inventory.description")}
            error={errors.description?.message}
            {...register("description", {
              validate: (value) => hasTrimmedValue(value) || t("common.required"),
            })}
          />

          <Field.Textarea
            label={`${t("inventory.improvementNotes")} (${t("common.optional")})`}
            info={t("inventory.improvementNotesInfo")}
            infoTitle={t("inventory.improvementNotes")}
            {...register("improvementNotes")}
          />
          <div className={styles.inlineGrid}>
            <Field.Input
              label={`${t("inventory.githubUrl")} (${t("common.optional")})`}
              type="url"
              error={errors.githubUrl?.message}
              {...register("githubUrl", {
                validate: (value) => isOptionalHttpUrl(value) || t("common.invalidUrl"),
              })}
            />
            <Field.Input
              label={`${t("inventory.deploymentLocation")} (${t("common.optional")})`}
              info={t("inventory.deploymentLocationInfo")}
              infoTitle={t("inventory.deploymentLocation")}
              {...register("deploymentLocation")}
            />
          </div>

          <Form.Field
            label={t("inventory.audience")}
            infoTitle={t("inventory.audience")}
            info={
              <>
                <p>
                  <strong>{t("inventory.audienceInternal")}</strong> - {t("requests.audienceInternalInfo")}
                </p>
                <p>
                  <strong>{t("inventory.audienceExternal")}</strong> - {t("requests.audienceExternalInfo")}
                </p>
              </>
            }
          >
            <Controller
              name="audience"
              control={control}
              render={({ field }) => (
                <FilterBar
                  value={field.value}
                  options={[
                    { value: "internal", label: t("inventory.audienceInternal") },
                    { value: "external", label: t("inventory.audienceExternal") },
                  ]}
                  onChange={field.onChange}
                />
              )}
            />
          </Form.Field>
          {audience === "external" && <Alert>{t("inventory.audienceExternalAlert")}</Alert>}
          <Controller
            name="participants"
            control={control}
            render={({ field }) => (
              <UserSelect
                label={t("inventory.participants")}
                selected={field.value}
                onChange={field.onChange}
                excludeIds={[item.createdBy.id]}
              />
            )}
          />

          {isAdmin && (
            <Section title={t("inventory.managementSection")}>
              <Form.Field label={t("inventory.status")}>
                <Controller
                  name="mgmtStatus"
                  control={control}
                  render={({ field }) => (
                    <FilterBar value={field.value} options={statusOptions} onChange={field.onChange} />
                  )}
                />
              </Form.Field>
              <Form.Field label={t("inventory.rejectCause")}>
                <Controller
                  name="rejectCause"
                  control={control}
                  render={({ field }) => (
                    <FilterBar<RejectCause>
                      value={field.value}
                      options={rejectCauseOptions}
                      onChange={field.onChange}
                      nullable
                      disabled={!isRejected}
                    />
                  )}
                />
              </Form.Field>
              <Field.Textarea
                label={`${t("inventory.rejectNote")} (${t("common.optional")})`}
                disabled={!isRejected}
                {...register("rejectNote")}
              />
            </Section>
          )}

          {updateItem.isError && <Form.Feedback type="error">{t("common.error")}</Form.Feedback>}

          <Form.Actions>
            <Button type="button" variant="secondary" onClick={() => navigate(`/inventory/${id}`)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={updateItem.isPending}>
              {t("common.save")}
            </Button>
          </Form.Actions>
        </Form>
      </Card>
    </Page>
  );
}
